import { callGeminiProxy, stripCodeFences } from './geminiProxy';

const FOLLOW_UP_VAR_RE = /\{\{[a-z0-9_]+\}\}/gi;

function extractFollowUpVars(text: string): string[] {
    return [...new Set(text.match(FOLLOW_UP_VAR_RE) ?? [])];
}

function softCleanFollowUpAiText(aiText: string): string {
    let text = stripCodeFences(aiText ?? '');
    if (!text) return '';

    if (
        (text.startsWith('"') && text.endsWith('"')) ||
        (text.startsWith("'") && text.endsWith("'"))
    ) {
        text = text.slice(1, -1).trim();
    }

    return text.trim();
}

function looksLikeJsonObject(text: string): boolean {
    if (!text.startsWith('{') || !text.endsWith('}')) return false;
    if (text.includes('{{')) return false;
    try {
        const parsed = JSON.parse(text) as unknown;
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
        return false;
    }
}

function validateFollowUpTemplateRewrite(aiText: string, original: string): string | null {
    let text = softCleanFollowUpAiText(aiText);
    if (!text) return null;
    if (looksLikeJsonObject(text)) return null;
    if (text.length > 600) return null;

    const missingVars = extractFollowUpVars(original).filter((tag) => !text.includes(tag));
    if (missingVars.length > 0) {
        text = `${text} ${missingVars.join(' ')}`.trim();
    }

    return text;
}

/**
 * Reescreve o texto de um template de follow-up no editor (não no envio).
 * Preserva tokens {{variavel}} do original.
 */
export const rewriteFollowUpTemplateMessage = async (
    originalText: string,
    options?: { hasMedia?: boolean }
): Promise<string> => {
    const original = (originalText ?? '').trim();
    if (!original) {
        throw new Error('Escreva uma mensagem antes de reescrever com IA.');
    }

    const mediaNote = options?.hasMedia
        ? `
CONTEXTO: Existe mídia anexada SEPARADAMENTE a esta mensagem (imagem/vídeo/áudio/documento).
Reescreva APENAS a legenda/texto. NÃO invente links, NÃO diga que vai anexar arquivo, NÃO mencione URLs.`
        : '';

    const prompt = `Você reescreve mensagens de follow-up WhatsApp em português do Brasil.
${mediaNote}

TEXTO ATUAL DO TEMPLATE:
${original}

REGRAS OBRIGATÓRIAS:
1. Responda APENAS com o corpo da mensagem (texto puro).
2. NÃO inclua assinatura, *Nome* nem prefixo de atendente.
3. NÃO invente preços, descontos, prazos, links ou fatos novos.
4. Mantenha a intenção do texto atual.
5. Preserve EXATAMENTE todos os tokens {{variavel}} que existirem no texto atual (ex.: {{nome_contato}}, {{titulo_negociacao}}, {{telefone}}). Copie-os sem alterar.
6. Sem HTML, sem JSON, sem blocos de código.
7. No máximo ~500 caracteres.

Saída:`;

    try {
        const rawText = await callGeminiProxy(prompt, { temperature: 0.35 });
        const validated = validateFollowUpTemplateRewrite(rawText, original);
        if (!validated) {
            throw new Error('A IA gerou um texto inválido. Tente de novo.');
        }
        return validated;
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Erro ao reescrever mensagem com IA.';
        console.error('Error rewriting follow-up template (Gemini proxy):', message);
        throw new Error(message);
    }
};
